import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { Link } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import {
  finishStatementReconciliation,
  reviewStatementReconciliation,
  type StatementReconciliationReview,
} from "@/lib/api";

const DISPLAY_CURRENCY = "USD";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function money(cents: number): string {
  return formatMoney({ cents, currency: DISPLAY_CURRENCY });
}

/** Dollars to cents, including zero and a negative statement balance. */
function statementCents(amount: string): number | null {
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(amount.trim());
  if (!match) return null;
  const whole = Number(match[2]);
  const fraction = Number((match[3] ?? "").padEnd(2, "0"));
  if (!Number.isSafeInteger(whole) || !Number.isSafeInteger(fraction)) return null;
  const cents = whole * 100 + fraction;
  if (!Number.isSafeInteger(cents)) return null;
  return match[1] === "-" ? -cents : cents;
}

function isIsoDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

export function StatementCheck({
  accountId,
  onFinished,
}: {
  accountId: string;
  onFinished?: () => void;
}) {
  const [endingDate, setEndingDate] = useState("");
  const [endingBalance, setEndingBalance] = useState("");
  const [reviewing, setReviewing] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [review, setReview] = useState<StatementReconciliationReview | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const submitReview = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!accountId) {
      setError("Choose a bank account first.");
      return;
    }
    if (!isIsoDate(endingDate)) {
      setError("Enter the statement ending date.");
      return;
    }
    const statement_ending_balance_cents = statementCents(endingBalance);
    if (statement_ending_balance_cents === null) {
      setError(
        "Enter the statement ending balance in dollars, such as 1250.40 or -25.00.",
      );
      return;
    }
    setReviewing(true);
    setError(null);
    setNotice(null);
    try {
      const result = await reviewStatementReconciliation(accountId, {
        statement_ending_date: endingDate,
        statement_ending_balance_cents,
      });
      setReview(result);
    } catch (cause) {
      setReview(null);
      setError(toErrorMessage(cause));
    } finally {
      setReviewing(false);
    }
  };

  const finishStatement = async () => {
    if (!accountId || !review?.finish_allowed || finishing) return;
    setFinishing(true);
    setError(null);
    setNotice(null);
    try {
      const saved = await finishStatementReconciliation(accountId, {
        statement_ending_date: review.statement_ending_date,
        statement_ending_balance_cents: review.statement_ending_balance_cents,
      });
      const amount = money(saved.statement_ending_balance_cents);
      const date = saved.statement_ending_date;
      setNotice(
        saved.already_finished
          ? `This statement was already finished through ${date} at ${amount}. The next statement begins at that balance.`
          : `This statement is finished through ${date} at ${amount}. The next statement begins at that balance. Matching records the match and does not move money.`,
      );
      onFinished?.();
    } catch (cause) {
      setError(toErrorMessage(cause));
    } finally {
      setFinishing(false);
    }
  };

  return (
    <section aria-label="Statement check" className="grid content-start gap-4">
      <div>
        <h2 className="text-lg font-semibold tracking-tight">
          Statement check
        </h2>
        <p className="mt-1 text-sm text-[var(--muted-foreground)]">
          The ledger balance is the posted balance of this account through the
          ending date. Matching a line records the match and does not move
          money. Finish stays off until that balance equals the statement and
          every line on or before the ending date is matched or excluded. An
          adjustment is a journal you categorize and confirm.
        </p>
      </div>

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
        </div>
      ) : null}
      {notice ? (
        <output className="surface block rounded-xl border border-[var(--status-success-foreground)] p-4 text-sm">
          {notice}
        </output>
      ) : null}

      <form
        onSubmit={submitReview}
        className="surface grid gap-3 rounded-2xl p-5"
      >
        <div className="flex flex-wrap items-end gap-3">
          <label
            htmlFor="statement-ending-date"
            className="grid gap-1 text-xs font-medium"
          >
            Statement ending date
            <Input
              id="statement-ending-date"
              type="date"
              value={endingDate}
              onChange={(event) => {
                setEndingDate(event.target.value);
                setReview(null);
              }}
              disabled={reviewing}
              required
            />
          </label>
          <label
            htmlFor="statement-ending-balance"
            className="grid gap-1 text-xs font-medium"
          >
            Statement ending balance
            <Input
              id="statement-ending-balance"
              inputMode="decimal"
              value={endingBalance}
              onChange={(event) => {
                setEndingBalance(event.target.value);
                setReview(null);
              }}
              placeholder="1250.40"
              disabled={reviewing}
              required
            />
          </label>
          <Button type="submit" disabled={reviewing || !accountId}>
            {reviewing ? "Reviewing statement…" : "Review statement"}
          </Button>
        </div>
        {review ? (
          <div className="grid gap-3" aria-live="polite">
            <dl className="grid gap-2 sm:grid-cols-3">
              <div>
                <dt className="text-xs text-[var(--muted-foreground)]">
                  Statement ending balance
                </dt>
                <dd>{money(review.statement_ending_balance_cents)}</dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--muted-foreground)]">
                  Ledger balance
                </dt>
                <dd>{money(review.book_balance_cents)}</dd>
              </div>
              <div>
                <dt className="text-xs text-[var(--muted-foreground)]">
                  Difference
                </dt>
                <dd>{money(review.difference_cents)}</dd>
              </div>
            </dl>
            <p className="text-sm text-[var(--muted-foreground)]">
              {review.open_line_count} open, {review.addressed_line_count}{" "}
              addressed, {review.later_line_count} after this statement.
            </p>
            <p className="text-sm">
              {review.beginning_balance_cents != null &&
              review.beginning_statement_ending_date
                ? `Beginning balance ${money(review.beginning_balance_cents)}, from the statement finished through ${review.beginning_statement_ending_date}.`
                : "No earlier statement is finished. Finishing this statement sets the next statement's beginning balance."}
            </p>
            {review.finish_allowed ? null : (
              <p className="text-sm">
                {review.difference_cents === 0
                  ? "Match or exclude each open line dated on or before the ending date."
                  : "The difference is the statement balance minus the ledger balance. Match or exclude the open lines, or post a journal you categorize. This screen does not write off the difference."}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                disabled={!review.finish_allowed || finishing}
                onClick={() => {
                  void finishStatement();
                }}
              >
                {finishing ? "Finishing reconciliation…" : "Finish reconciliation"}
              </Button>
              <Button asChild variant="secondary">
                <Link to="/journal">Post an adjustment journal</Link>
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-[var(--muted-foreground)]">
            No statement check yet. Enter the date and balance from the
            statement, then review.
          </p>
        )}
      </form>
    </section>
  );
}
